import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "./AboutServices.css";
import { FiChevronDown } from "react-icons/fi";

import { useTranslation } from "react-i18next";
import { getPageContent } from "../../../../api/content";
import { resolveMediaUrl } from "../../../../utils/media";

const itemFieldRegex = /^item(\d+)([a-zA-Z0-9_]+)$/i;
const cardFieldRegex = /^card(\d+)([a-zA-Z0-9_]+)$/i;

const tryParseJsonObject = (str) => {
  const t = String(str || "").trim();
  if (!t.startsWith("{") || !t.endsWith("}")) return null;
  try {
    const o = JSON.parse(t);
    return o && typeof o === "object" && !Array.isArray(o) ? o : null;
  } catch {
    return null;
  }
};

const coalesceServicesPayload = (raw) => {
  if (raw == null) return null;
  if (typeof raw === "string") {
    return coalesceServicesPayload(tryParseJsonObject(raw) || {});
  }
  if (typeof raw !== "object") return null;

  const mergeServicesWrapper = (node, into) => {
    const wrap =
      node?.ourServices ??
      node?.ourservices ??
      node?.OurServices ??
      node?.aboutServices ??
      node?.aboutservices ??
      node?.AboutServices;
    if (!wrap || typeof wrap !== "object") return into;
    if (wrap.data && typeof wrap.data === "object" && !Array.isArray(wrap.data)) {
      return { ...into, ...wrap.data };
    }
    return { ...into, ...wrap };
  };

  let base = { ...raw };
  base = mergeServicesWrapper(raw, base);
  if (raw.data && typeof raw.data === "object" && !Array.isArray(raw.data)) {
    base = { ...base, ...raw.data };
    base = mergeServicesWrapper(raw.data, base);
  }
  if (typeof raw.value === "string") {
    const parsed = tryParseJsonObject(raw.value);
    if (parsed) base = { ...base, ...parsed };
  } else if (raw.value && typeof raw.value === "object" && !Array.isArray(raw.value)) {
    base = { ...base, ...raw.value };
  }
  return mergeServicesWrapper(base, base);
};

const normalizeSectionSlug = (key) =>
  String(key || "")
    .toLowerCase()
    .replace(/[_\s-]+/g, "");

const findPageSection = (root, slug) => {
  if (!root || typeof root !== "object") return undefined;
  const target = normalizeSectionSlug(slug);
  const hit = Object.keys(root).find((k) => normalizeSectionSlug(k) === target);
  return hit != null ? root[hit] : undefined;
};

const pickString = (...candidates) => {
  for (const c of candidates) {
    if (typeof c === "string" && c.trim()) return c.trim();
  }
  return "";
};

const hasMeaningfulServicesPayload = (c) => {
  if (!c || typeof c !== "object") return false;
  if (pickString(c.tag, c.sectionTag, c.title, c.heading, c.description, c.body, c.text)) return true;
  return Object.keys(c).some((k) => itemFieldRegex.test(k) || cardFieldRegex.test(k));
};

const getCardIconFromApi = (content, itemNumber) => {
  const suffix = String(itemNumber);
  const candidates = [
    content?.[`card${suffix}Icon`],
    content?.[`Card${suffix}Icon`],
    content?.[`card_${suffix}_icon`],
    content?.[`item${suffix}Icon`],
    content?.[`item${suffix}icon`],
    content?.[`item${suffix}Image`],
    content?.[`item${suffix}image`],
    content?.[`item${suffix}Media`],
    content?.[`item${suffix}media`],
    content?.[`item${suffix}MediaUrl`],
    content?.[`item${suffix}mediaUrl`],
  ];
  for (const candidate of candidates) {
    const resolved = resolveMediaUrl(candidate);
    if (resolved) return resolved;
  }
  return "";
};

const AboutServices = ({ content }) => {
  const { i18n } = useTranslation();
  const [apiContent, setApiContent] = useState(null);

  useEffect(() => {
    if (content && typeof content === "object") return undefined;

    let isMounted = true;
    const load = async () => {
      try {
        const data = await getPageContent({
          page: "about",
          language: (i18n.language || "en").toLowerCase(),
        });
        if (!isMounted) return;
        const section =
          findPageSection(data || {}, "aboutservices") ||
          findPageSection(data || {}, "ourservices");
        setApiContent(section ?? null);
      } catch {
        if (isMounted) setApiContent(null);
      }
    };
    load();
    return () => {
      isMounted = false;
    };
  }, [content, i18n.language]);

  const section = useMemo(() => {
    const source = content ?? apiContent;
    const payload = coalesceServicesPayload(source);
    if (!hasMeaningfulServicesPayload(payload) || !payload) return null;
    return payload;
  }, [content, apiContent]);

  const services = useMemo(() => {
    if (!section) return [];
    const cardsMap = new Map();

    const ingestField = (index, fieldRaw, value) => {
      const field = String(fieldRaw || "").replace(/[_\s-]+/g, "").toLowerCase();
      if (!cardsMap.has(index)) cardsMap.set(index, {});
      cardsMap.get(index)[field] = value;
    };

    Object.entries(section).forEach(([key, value]) => {
      const itemMatch = key.match(itemFieldRegex);
      if (itemMatch) {
        ingestField(Number(itemMatch[1]), itemMatch[2], value);
        return;
      }
      const cardMatch = key.match(cardFieldRegex);
      if (cardMatch) {
        ingestField(Number(cardMatch[1]), cardMatch[2], value);
      }
    });

    return Array.from(cardsMap.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([index, fields]) => {
        const icon =
          getCardIconFromApi(section, index) ||
          resolveMediaUrl(fields.icon || fields.image || fields.media || fields.mediaurl) ||
          "";
        return {
          index,
          icon,
          title: pickString(fields.title, fields.heading),
          subtitle: pickString(fields.subtitle, fields.subheading),
          text: pickString(fields.text, fields.description, fields.body),
        };
      })
      .filter((item) => item.title || item.subtitle || item.text || item.icon);
  }, [section]);

  if (!section) return null;

  const tag = pickString(section.tag, section.sectionTag);
  const sectionTitle = pickString(
    section.title,
    section.heading,
    [section.titleLine1, section.titleLine2].filter(Boolean).join(" ").trim()
  );
  const sectionDescription = pickString(section.description, section.body, section.text, section.summary);
  const showMoreText = pickString(section.showMoreText, section.cardButtonText);

  if (!tag && !sectionTitle && !sectionDescription && !services.length) return null;

  return (
    <section className="aboutservices">
      <div className="aboutservices-container">
        {tag || sectionTitle || sectionDescription ? (
          <div className="aboutservices-top">
            {tag ? <span className="aboutservices-tag">{tag}</span> : null}
            {sectionTitle ? <h2>{sectionTitle}</h2> : null}
            {sectionDescription ? <p>{sectionDescription}</p> : null}
          </div>
        ) : null}

        {services.length > 0 ? (
          <div className="aboutservices-grid">
            {services.map((item) => (
              <div className="aboutservices-card" key={`aboutservices-${item.index}`}>
                {item.icon ? (
                  <img
                    src={item.icon}
                    alt=""
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                ) : null}
                {item.title ? <h3>{item.title}</h3> : null}
                {item.subtitle ? <span className="aboutservices-sub">{item.subtitle}</span> : null}
                {item.text ? <p>{item.text}</p> : null}
                {showMoreText ? (
                  <Link to="/services" className="aboutservices-showMore">
                    <span>{showMoreText}</span>
                    <FiChevronDown />
                  </Link>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default AboutServices;
